import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { OrderEnvelope } from "@tilt/pos-sdk";

import { getPosClient } from "../../lib/api";
import { useFocusTrap } from "../../lib/use-focus-trap";
import { Alert } from "../ui/Alert";
import { Button } from "../ui/Button";
import { Field, TextInput } from "../ui/Field";

/**
 * Cancel-pending-payment dialog (#185).
 *
 * Posted to ``POST /pos/v1/orders/{id}/payments/{payment_id}/cancel``
 * with a required ``reason``. The settlement-poller continues to
 * watch the row for vendor late-completion until either the
 * consecutive-unknown threshold elapses or a late completion is
 * surfaced - both are visible on the Timeline row.
 *
 * UX shape mirrors EditOrderDialog: centered modal, X close,
 * primary destructive action ("Cancel payment") + quiet secondary
 * ("Keep waiting"). The reason field is required + max 500 chars
 * (matches the API contract).
 */

const REASON_MAX = 500;

export interface CancelPaymentDialogProps {
  readonly orderId: string;
  readonly paymentId: string;
  readonly onClose: () => void;
  readonly onCancelled: (envelope: OrderEnvelope) => void;
}

export function CancelPaymentDialog({
  orderId,
  paymentId,
  onClose,
  onCancelled,
}: CancelPaymentDialogProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useFocusTrap<HTMLDivElement>(true, onClose);

  const mutation = useMutation({
    mutationFn: () => getPosClient().cancelPayment(orderId, paymentId, { reason: reason.trim() }),
    onSuccess: (env) => {
      // Mirror the createPayment cache invalidation: the order
      // detail + transactions list both key on ``["orders"]`` so a
      // single broad invalidate refreshes both surfaces.
      void qc.invalidateQueries({ queryKey: ["orders"] });
      onCancelled(env);
    },
    onError: (err: unknown) => {
      if (err instanceof Error) {
        const body = (err as Error & { body?: { detail?: string; error?: string } }).body;
        setError(body?.detail ?? body?.error ?? err.message);
      } else {
        setError("Something went wrong.");
      }
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!reason.trim()) {
      setError(t("sale.cancelPaymentReasonRequired"));
      return;
    }
    setError(null);
    mutation.mutate();
  }

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-payment-dialog-title"
    >
      <div className="relative w-full max-w-md rounded-xl bg-surface p-6 shadow-elev-3">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 rounded-md p-1 text-muted hover:bg-surface-muted hover:text-strong"
        >
          <X className="h-4 w-4" />
        </button>

        <h2
          id="cancel-payment-dialog-title"
          className="text-lg font-bold tracking-tight text-strong"
        >
          {t("sale.cancelPaymentDialogTitle")}
        </h2>
        <p className="mt-2 text-sm text-muted">{t("sale.cancelPaymentDialogBody")}</p>

        <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-4">
          <Field label={t("sale.cancelPaymentReason")}>
            <TextInput
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("sale.cancelPaymentReasonPlaceholder")}
              maxLength={REASON_MAX}
              autoFocus
              disabled={mutation.isPending}
            />
          </Field>

          {error && <Alert tone="error">{error}</Alert>}

          <div className="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={mutation.isPending}
              className="min-h-tap"
            >
              {t("sale.cancelPaymentKeep")}
            </Button>
            <Button
              type="submit"
              variant="danger"
              disabled={mutation.isPending || !reason.trim()}
              loading={mutation.isPending}
              className="min-h-tap"
            >
              {t("sale.cancelPaymentSubmit")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
